import type { Rng } from '../../core/rng';
import type { LootDrop } from '../../game/loot';
import { rollGear } from '../../game/loot';
import type { Progression } from '../../game/Progression';
import type { Rarity } from '../../game/items';
import type { RewardsState } from '../../ui/app/store';

/** Loot helpers shared by the activities (personal rewards, granted on each peer). */

export const emptyDrop = (): LootDrop => ({ items: [], equip: [], silver: 0, gold: 0, cards: [] });

/** Adds `b` into `a` (stacks of the same item merge). */
export function mergeDrop(a: LootDrop, b: LootDrop): LootDrop {
  a.silver += b.silver;
  a.gold += b.gold;
  for (const it of b.items) addItems(a, it.id, it.count);
  a.equip.push(...b.equip);
  a.cards.push(...b.cards);
  return a;
}

export function addItems(d: LootDrop, id: string, count: number): LootDrop {
  if (count <= 0) return d;
  const cur = d.items.find((x) => x.id === id);
  if (cur) cur.count += count;
  else d.items.push({ id, count });
  return d;
}

/** Scales a drop by `f` (partial clears): stacks and coins shrink; gear only survives past half. */
export function scaleDrop(d: LootDrop, f: number): LootDrop {
  const k = Math.max(0, Math.min(1, f));
  return {
    silver: Math.round(d.silver * k),
    gold: Math.round(d.gold * k),
    items: d.items.map((it) => ({ id: it.id, count: Math.floor(it.count * k) })).filter((it) => it.count > 0),
    equip: k >= 0.5 ? d.equip.slice(0, 1) : [],
    cards: [],
  };
}

/** A "gear chest": one gear piece of the content level (epic–relic). */
export function gearChest(level: number, rng: Rng): LootDrop {
  const rarity = rng.weighted<Rarity>([
    { weight: 6, value: 'epic' },
    { weight: 3, value: 'legendary' },
    { weight: 1, value: 'relic' },
  ]);
  return { ...emptyDrop(), equip: [rollGear(level, rarity, rng)] };
}

/** Grants a drop to the local character and returns the overlay state describing it. */
export function grantDrop(p: Progression, drop: LootDrop, source: string, title: string, subtitle: string): RewardsState {
  p.grantDrop(drop, source);
  return rewardsOf(drop, title, subtitle);
}

export function rewardsOf(drop: LootDrop, title: string, subtitle: string): RewardsState {
  return {
    title,
    subtitle,
    silver: drop.silver,
    gold: drop.gold,
    items: drop.items.map((it) => ({ ...it })),
    equip: drop.equip.map((e) => ({ name: e.name, rarity: e.rarity })),
    cards: [...drop.cards],
  };
}
